import {
  useDocuments,
  useDocumentDownload,
  useDocumentDelete,
  useDocumentExportZip,
} from '../hooks';
import { useDocumentsStore } from '../store';
import { DocumentContext, Document } from '../types';
import { DocumentGrid } from './DocumentGrid';
import { DocumentFilters } from './DocumentFilters';
import { EmptyDocumentsState } from './EmptyDocumentsState';
import { DocumentUploadModal } from './DocumentUploadModal';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { GedCreateModal } from './GedCreateModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { Button } from '@/components/ui/Button';
import { Plus, FileText, FolderDown } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useOrganisationMembers } from '@/hooks/useOrganisations';
import { useProfile } from '@/hooks/useAuth';

interface DocumentsViewProps {
  context?: DocumentContext;
  contextUuid?: string;
  contextName?: string;
}

export const DocumentsView: React.FC<DocumentsViewProps> = ({
  context,
  contextUuid,
  contextName,
}) => {
  const {
    searchQuery,
    selectedCategory,
    selectedTags,
    currentPage,
    isUploadModalOpen,
    isCreateModalOpen,
    isPreviewModalOpen,
    isDeleteModalOpen,
    deleteDocument,
    openUploadModal,
    openCreateModal,
    openPreviewModal,
    openDeleteModal,
    closeDeleteModal,
  } = useDocumentsStore();

  // Build query params
  const queryParams = useMemo(
    () => ({
      context,
      contextUuid,
      page: currentPage,
      limit: 20,
      category: selectedCategory || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      search: searchQuery || undefined,
      sortBy: 'createdAt' as const,
      sortOrder: 'DESC' as const,
    }),
    [
      context,
      contextUuid,
      currentPage,
      searchQuery,
      selectedCategory,
      selectedTags,
    ]
  );

  const { data, isLoading, error } = useDocuments(queryParams);

  // Organisation role check
  const { data: profileData } = useProfile();
  const profileUser = profileData?.data;
  const currentOrganisation = useOrganisationsStore(s => s.currentOrganisation);
  const { data: members } = useOrganisationMembers(
    currentOrganisation?.uuid || ''
  );
  const isOrgAdmin = useMemo(() => {
    if (!profileUser || !members) return false;
    const userUuid = profileUser.uuid || profileUser.id;
    const member = members.find(m => m.userUuid === userUuid);
    return member?.role === 'owner' || member?.role === 'admin';
  }, [profileUser, members]);

  // Hooks pour les actions sur les documents
  const { mutate: downloadDocument } = useDocumentDownload();
  const { mutate: deleteDocumentMutation } = useDocumentDelete();
  const { mutate: exportZip, isPending: isExporting } = useDocumentExportZip();

  const handlePreview = useCallback(
    (uuid: string) => {
      openPreviewModal(uuid);
    },
    [openPreviewModal]
  );

  const handleDownload = useCallback(
    (document: Document) => {
      console.log('handleDownload called with:', document);
      downloadDocument(document);
      console.log('downloadDocument mutation triggered');
    },
    [downloadDocument]
  );

  const handleDelete = useCallback(
    (uuid: string) => {
      console.log('handleDelete called with uuid:', uuid);
      // Trouver le document pour ouvrir le modal de confirmation
      const document = data?.data.find(doc => doc.uuid === uuid);
      if (document) {
        openDeleteModal(document);
      }
    },
    [openDeleteModal, data]
  );

  const handleConfirmDelete = useCallback(() => {
    if (deleteDocument) {
      console.log('User confirmed, calling deleteDocument');
      deleteDocumentMutation(deleteDocument.uuid);
      console.log('deleteDocument mutation triggered');
      closeDeleteModal();
    }
  }, [deleteDocument, deleteDocumentMutation, closeDeleteModal]);

  const handleExportZip = useCallback(() => {
    exportZip(queryParams);
  }, [exportZip, queryParams]);

  const handleEdit = useCallback((document: Document) => {
    // TODO: Ouvrir un modal d'édition des métadonnées
    toast.info("Fonctionnalité d'édition en cours de développement");
    console.log('Edit document:', document);
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-neutral-900">Documents</h2>
        <div className="flex gap-2">
          {isOrgAdmin && data && data.data.length > 0 && (
            <Button
              onClick={handleExportZip}
              variant="outline"
              disabled={isExporting}
            >
              <FolderDown className="mr-2 h-4 w-4" />
              {isExporting ? 'Export...' : 'Export ZIP'}
            </Button>
          )}
          {context && contextUuid && (
            <>
              <Button onClick={openCreateModal} variant="secondary">
                <FileText className="mr-2 h-4 w-4" />
                Créer
              </Button>
              <Button onClick={openUploadModal} variant="primary">
                <Plus className="mr-2 h-4 w-4" />
                Upload
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <DocumentFilters />

      {/* Content */}
      {isLoading ? (
        <div className="py-12 text-center text-neutral-500">
          Chargement des documents...
        </div>
      ) : error ? (
        <div className="bg-error-50 text-error-800 rounded-lg p-4">
          Erreur lors du chargement des documents
        </div>
      ) : !data || data.data.length === 0 ? (
        <EmptyDocumentsState context={contextName} />
      ) : (
        <>
          <DocumentGrid
            documents={data.data}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                Page {data.page} sur {data.totalPages} ({data.total} documents)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={!data.hasPreviousPage}
                  onClick={() => {
                    // TODO: Implement pagination
                  }}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  disabled={!data.hasNextPage}
                  onClick={() => {
                    // TODO: Implement pagination
                  }}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {isCreateModalOpen && context && contextUuid && (
        <GedCreateModal context={context} contextUuid={contextUuid} />
      )}

      {isUploadModalOpen && context && contextUuid && (
        <DocumentUploadModal
          context={context}
          contextUuid={contextUuid}
          contextName={contextName}
        />
      )}

      {isPreviewModalOpen && <DocumentPreviewModal />}

      {isDeleteModalOpen && deleteDocument && (
        <DeleteConfirmModal
          document={deleteDocument}
          onConfirm={handleConfirmDelete}
          onCancel={closeDeleteModal}
        />
      )}
    </div>
  );
};
